import { useMemo, useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { Bell, Mail, MessageSquare, Pencil, Phone, Plus, Smartphone, Trash2 } from 'lucide-react'
import { toast } from 'sonner'

import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import { Skeleton } from '@/components/ui/skeleton'
import { getErrorMessage } from '@/lib/api'
import {
  financeApi,
  type NotificationContactRow,
  type NotificationEventOption,
} from '@/services/finance'

type ContactFormState = {
  label: string
  notification_key: string
  email: string
  phone: string
  whatsapp: string
  notify_email: boolean
  notify_sms: boolean
  notify_whatsapp: boolean
  notify_push: boolean
}

const defaultForm = (notificationKey = 'job_settlement_invoice_notification'): ContactFormState => ({
  label: '',
  notification_key: notificationKey,
  email: '',
  phone: '',
  whatsapp: '',
  notify_email: true,
  notify_sms: false,
  notify_whatsapp: false,
  notify_push: false,
})

type SettlementNotificationContactsPanelProps = {
  serviceInstanceId?: string | null
  embedded?: boolean
}

function ChannelBadges({ contact }: { contact: NotificationContactRow }) {
  const channels: string[] = []
  if (contact.notify_email) channels.push('Email')
  if (contact.notify_sms) channels.push('SMS')
  if (contact.notify_whatsapp) channels.push('WhatsApp')
  if (contact.notify_push) channels.push('Push')
  if (channels.length === 0) return <span className="text-muted-foreground">—</span>
  return (
    <div className="flex flex-wrap gap-1">
      {channels.map((channel) => (
        <Badge key={channel} variant="secondary" className="text-xs">
          {channel}
        </Badge>
      ))}
    </div>
  )
}

function ToggleRow({
  id,
  label,
  checked,
  onChange,
}: {
  id: string
  label: string
  checked: boolean
  onChange: (checked: boolean) => void
}) {
  return (
    <label htmlFor={id} className="flex cursor-pointer items-center gap-2 text-sm">
      <input
        id={id}
        type="checkbox"
        className="h-4 w-4 rounded border-border"
        checked={checked}
        onChange={(event) => onChange(event.target.checked)}
      />
      {label}
    </label>
  )
}

export function SettlementNotificationContactsPanel({
  serviceInstanceId,
  embedded = false,
}: SettlementNotificationContactsPanelProps) {
  const queryClient = useQueryClient()
  const [eventFilter, setEventFilter] = useState('all')
  const [dialogOpen, setDialogOpen] = useState(false)
  const [editingContact, setEditingContact] = useState<NotificationContactRow | null>(null)
  const [form, setForm] = useState<ContactFormState>(defaultForm())

  const eventsQuery = useQuery({
    queryKey: ['notification-events'],
    queryFn: () => financeApi.getNotificationEvents(),
  })

  const eventOptions = useMemo(() => {
    const list = eventsQuery.data?.list ?? []
    return list as NotificationEventOption[]
  }, [eventsQuery.data])

  const contactsQuery = useQuery({
    queryKey: ['notification-contacts', serviceInstanceId, eventFilter],
    queryFn: () =>
      financeApi.getNotificationContacts({
        service_instance_id: serviceInstanceId ?? undefined,
        notification_key: eventFilter !== 'all' ? eventFilter : undefined,
      }),
    enabled: Boolean(serviceInstanceId),
  })

  const contacts = contactsQuery.data?.results ?? []

  const resetDialog = () => {
    setDialogOpen(false)
    setEditingContact(null)
    setForm(defaultForm(eventFilter !== 'all' ? eventFilter : undefined))
  }

  const openCreate = () => {
    setEditingContact(null)
    setForm(defaultForm(eventFilter !== 'all' ? eventFilter : undefined))
    setDialogOpen(true)
  }

  const openEdit = (contact: NotificationContactRow) => {
    setEditingContact(contact)
    setForm({
      label: contact.label ?? '',
      notification_key: contact.notification_key,
      email: contact.email ?? '',
      phone: contact.phone ?? '',
      whatsapp: contact.whatsapp ?? '',
      notify_email: contact.notify_email,
      notify_sms: contact.notify_sms,
      notify_whatsapp: contact.notify_whatsapp,
      notify_push: contact.notify_push,
    })
    setDialogOpen(true)
  }

  const saveMutation = useMutation({
    mutationFn: async () => {
      if (!serviceInstanceId) throw new Error('Select a service instance')
      const payload = {
        ...form,
        service_instance_id: serviceInstanceId,
      }
      if (editingContact) {
        return financeApi.updateNotificationContact(editingContact.id, {
          label: form.label,
          email: form.email,
          phone: form.phone,
          whatsapp: form.whatsapp,
          notify_email: form.notify_email,
          notify_sms: form.notify_sms,
          notify_whatsapp: form.notify_whatsapp,
          notify_push: form.notify_push,
        })
      }
      return financeApi.addNotificationContact(payload)
    },
    onSuccess: () => {
      toast.success(editingContact ? 'Notification contact updated' : 'Notification contact added')
      resetDialog()
      queryClient.invalidateQueries({ queryKey: ['notification-contacts'] })
    },
    onError: (error) => toast.error(getErrorMessage(error)),
  })

  const removeMutation = useMutation({
    mutationFn: (contactId: number) => financeApi.removeNotificationContact(contactId),
    onSuccess: () => {
      toast.success('Notification contact removed')
      queryClient.invalidateQueries({ queryKey: ['notification-contacts'] })
    },
    onError: (error) => toast.error(getErrorMessage(error)),
  })

  if (!serviceInstanceId) {
    return null
  }

  return (
    <Card className={embedded ? 'border-border/70 shadow-none' : undefined}>
      <CardHeader className="flex flex-row items-start justify-between gap-3 space-y-0">
        <div>
          <CardTitle className="flex items-center gap-2 text-base">
            {!embedded ? <Bell className="h-4 w-4" /> : null}
            Invoice notification contacts
            {contacts.length > 0 ? (
              <Badge variant="secondary" className="font-normal">
                {contacts.length} subscription{contacts.length === 1 ? '' : 's'}
              </Badge>
            ) : null}
          </CardTitle>
          <CardDescription>
            Subscribers on this instance when it appears as debtor or creditor on a generated invoice. The other
            company configures their own list on their service instance.
          </CardDescription>
        </div>
        <Button size="sm" onClick={openCreate}>
          <Plus className="mr-2 h-4 w-4" />
          Add contact
        </Button>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="flex flex-wrap items-center gap-3">
          <Label className="text-sm text-muted-foreground">Event</Label>
          <Select value={eventFilter} onValueChange={setEventFilter}>
            <SelectTrigger className="w-[280px]">
              <SelectValue placeholder="All events" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All events</SelectItem>
              {eventOptions.map((event) => (
                <SelectItem key={event.key} value={event.key}>
                  {event.text}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        {contactsQuery.isLoading ? (
          <Skeleton className="h-32 w-full" />
        ) : contacts.length === 0 ? (
          <p className="rounded-lg border border-dashed border-border/70 p-6 text-sm text-muted-foreground">
            No notification contacts configured. Add one or more recipients for{' '}
            <strong>Job Settlement Invoice Notification</strong> (same instance can have many subscriptions per event).
          </p>
        ) : (
          <div className="overflow-x-auto rounded-lg border border-border/70">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Label</TableHead>
                  <TableHead>Event</TableHead>
                  <TableHead>Destinations</TableHead>
                  <TableHead>Channels</TableHead>
                  <TableHead className="w-[100px]" />
                </TableRow>
              </TableHeader>
              <TableBody>
                {contacts.map((contact) => (
                  <TableRow key={contact.id}>
                    <TableCell className="font-medium">{contact.label || '—'}</TableCell>
                    <TableCell>
                      <span className="text-sm">{contact.notification_label ?? contact.notification_key}</span>
                    </TableCell>
                    <TableCell>
                      <div className="space-y-1 text-xs text-muted-foreground">
                        {contact.email ? (
                          <span className="flex items-center gap-1">
                            <Mail className="h-3 w-3" />
                            {contact.email}
                          </span>
                        ) : null}
                        {contact.phone ? (
                          <span className="flex items-center gap-1">
                            <Phone className="h-3 w-3" />
                            {contact.phone}
                          </span>
                        ) : null}
                        {contact.whatsapp ? (
                          <span className="flex items-center gap-1">
                            <MessageSquare className="h-3 w-3" />
                            {contact.whatsapp}
                          </span>
                        ) : null}
                        {contact.notify_push && !contact.email && !contact.phone && !contact.whatsapp ? (
                          <span className="flex items-center gap-1">
                            <Smartphone className="h-3 w-3" />
                            Admin push only
                          </span>
                        ) : null}
                      </div>
                    </TableCell>
                    <TableCell>
                      <ChannelBadges contact={contact} />
                    </TableCell>
                    <TableCell>
                      <div className="flex justify-end gap-1">
                        <Button type="button" size="icon" variant="ghost" onClick={() => openEdit(contact)}>
                          <Pencil className="h-4 w-4" />
                        </Button>
                        <Button
                          type="button"
                          size="icon"
                          variant="ghost"
                          className="text-destructive"
                          onClick={() => removeMutation.mutate(contact.id)}
                          disabled={removeMutation.isPending}
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        )}
      </CardContent>

      <Dialog open={dialogOpen} onOpenChange={(open) => (open ? setDialogOpen(true) : resetDialog())}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>{editingContact ? 'Edit notification contact' : 'Add notification contact'}</DialogTitle>
            <DialogDescription>
              Each row is a separate subscription. You can add many contacts for the same event on this service
              instance.
            </DialogDescription>
          </DialogHeader>
          <div className="grid gap-4 py-2">
            <div className="grid gap-2">
              <Label htmlFor="contact-label">Label</Label>
              <Input
                id="contact-label"
                placeholder="Finance team"
                value={form.label}
                onChange={(event) => setForm((current) => ({ ...current, label: event.target.value }))}
              />
            </div>
            {!editingContact ? (
              <div className="grid gap-2">
                <Label>Notification event</Label>
                <Select
                  value={form.notification_key}
                  onValueChange={(value) => setForm((current) => ({ ...current, notification_key: value }))}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {eventOptions.map((event) => (
                      <SelectItem key={event.key} value={event.key}>
                        {event.text}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            ) : null}
            <div className="grid gap-2">
              <Label htmlFor="contact-email">Email</Label>
              <Input
                id="contact-email"
                type="email"
                value={form.email}
                onChange={(event) => setForm((current) => ({ ...current, email: event.target.value }))}
              />
            </div>
            <div className="grid gap-2">
              <Label htmlFor="contact-phone">Phone (SMS)</Label>
              <Input
                id="contact-phone"
                value={form.phone}
                onChange={(event) => setForm((current) => ({ ...current, phone: event.target.value }))}
              />
            </div>
            <div className="grid gap-2">
              <Label htmlFor="contact-whatsapp">WhatsApp</Label>
              <Input
                id="contact-whatsapp"
                value={form.whatsapp}
                onChange={(event) => setForm((current) => ({ ...current, whatsapp: event.target.value }))}
              />
            </div>
            <div className="grid grid-cols-2 gap-3 pt-1">
              <ToggleRow
                id="notify-email"
                label="Email"
                checked={form.notify_email}
                onChange={(checked) => setForm((current) => ({ ...current, notify_email: checked }))}
              />
              <ToggleRow
                id="notify-sms"
                label="SMS"
                checked={form.notify_sms}
                onChange={(checked) => setForm((current) => ({ ...current, notify_sms: checked }))}
              />
              <ToggleRow
                id="notify-whatsapp"
                label="WhatsApp"
                checked={form.notify_whatsapp}
                onChange={(checked) => setForm((current) => ({ ...current, notify_whatsapp: checked }))}
              />
              <ToggleRow
                id="notify-push"
                label="Push (admins)"
                checked={form.notify_push}
                onChange={(checked) => setForm((current) => ({ ...current, notify_push: checked }))}
              />
            </div>
          </div>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={resetDialog}>
              Cancel
            </Button>
            <Button type="button" onClick={() => saveMutation.mutate()} disabled={saveMutation.isPending}>
              {editingContact ? 'Save changes' : 'Add contact'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Card>
  )
}
